import {
  Box,
  Button,
  useToast,
  Flex,
  Stack,
  Image,
  Heading,
  Text,
  Link,
} from '@chakra-ui/react';
import {
  DataTable,
  DataTableListOptions,
  DataTableSortingRule,
} from 'features/common/components/DataTable';
import EmptyState from 'features/common/components/EmptyState';
import { sortAuditLogs, tableOptions } from './utils';
import { useGetLogs } from './hooks/useGetLogs';
import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { AuditLog, AuditLogsResponse } from './types';
import { AxiosResponse } from 'axios';
import { AuditLogsSupportedSystemName } from './constants';
import { auditLogsApi } from './api/endpoints';
import { AuditLogDetailsDrawer } from './AuditLogDetailsDrawer';

interface LoadMoreButtonProps {
  onClick: () => void;
  isLoading: boolean;
}

const LoadMoreButton = ({ onClick, isLoading }: LoadMoreButtonProps) => (
  <Box textAlign='center' pb={6}>
    <Button
      isLoading={isLoading}
      variant='outline'
      aria-label='load more audit logs'
      size='sm'
      data-testid='load-more-audit-logs-button'
      onClick={onClick}
    >
      Load More
    </Button>
  </Box>
);

interface AuditLogsTableProps {
  system: AuditLogsSupportedSystemName;
  searchTerm?: string;
  tenantId?: string;
  onError?: () => void;
  onSuccess?: () => void;
}

const isSameSort = (
  current: DataTableSortingRule[],
  next: DataTableSortingRule[] = [],
) => {
  if (current === next) return true;
  if (current.length !== next.length) return false;

  return current.every(
    (rule, index) =>
      rule.id === next[index]?.id && !!rule.desc === !!next[index]?.desc,
  );
};

export const AuditLogsTable = forwardRef(
  (
    {
      system,
      searchTerm,
      tenantId,
      onError: onLogsError,
      onSuccess: onLogsSuccess,
    }: AuditLogsTableProps,
    ref: Ref<{ reloadData: () => void }>,
  ) => {
    const toast = useToast();
    const [continuationToken, setContinuationToken] = useState('');
    const [tableData, setTableData] = useState<AuditLog[]>([]);
    const [reloadLoading, setReloadLoading] = useState(false);
    const [hasMoreData, setHasMoreData] = useState(false);
    const [sortByState, setSortByState] = useState<DataTableSortingRule[]>([
      { id: 'logTime', desc: true },
    ]);
    const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
    const [hasRetried, setHasRetried] = useState(false);

    useEffect(() => {
      setContinuationToken('');
      setTableData([]);
      setHasMoreData(false);
    }, [system, tenantId]);

    const onError = () => {
      toast({
        description: 'Failed to load audit logs. Please try again.',
        title: 'Error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      onLogsError?.();
    };

    const onSuccess = (res: AxiosResponse<AuditLogsResponse>) => {
      if (res.data.data.length) {
        setTableData((currTableData) => [...currTableData, ...res.data.data]);
      }

      setHasMoreData(!!res.data.continuationToken);
      onLogsSuccess?.();
    };

    const {
      data: response,
      isLoading,
      isError,
      refetch,
      error,
    } = useGetLogs({
      onSuccess,
      onError,
      system,
      continuationToken,
      tenantId,
    });

    const getErrorDetails = () => {
      if (!error || typeof error !== 'object') return undefined;
      const err = error as {
        response?: { status?: number; data?: { detail?: string } };
      };
      return {
        status: err.response?.status,
        detail: err.response?.data?.detail,
      };
    };

    const reloadData = useCallback(() => {
      setSortByState([{ id: 'logTime', desc: true }]);
      setReloadLoading(true);
      setContinuationToken('');
      setTableData([]);
      setHasMoreData(false);

      refetch({
        queryKey: [
          auditLogsApi.get_logs.url({
            system,
            continuationToken: '',
            tenantId,
          }),
        ],
      }).finally(() => {
        setReloadLoading(false);
      });
    }, [refetch, system, tenantId, setSortByState]);

    useImperativeHandle(ref, () => ({
      reloadData,
    }));

    const responseData = response?.data;
    const initialRows: AuditLog[] = responseData?.data ?? [];
    const allRows = tableData.length > 0 ? tableData : initialRows;

    const handleTableChange = useCallback(
      ({ sortBy }: DataTableListOptions) => {
        // Keep the table's sort array when it changed. Replacing it with a new
        // array (for example to add a tie-break) makes DataTable call onChange
        // again, because it compares sort state by reference.
        setSortByState((current) =>
          isSameSort(current, sortBy) ? current : sortBy,
        );
      },
      [],
    );

    const filteredTableData = useMemo(() => {
      if (!searchTerm) return allRows;

      const lowerSearchTerm = searchTerm.toLowerCase();
      return allRows.filter((log) => {
        const actor =
          log.sourceSystemUserContext?.id ??
          log.sourceSystemUserContext?.anonymousId ??
          '';
        const action = log.action ?? '';
        const user = log.entity?.id ?? '';
        const environment = log.environmentId ?? '';
        const roles = log.extensions?.roles;
        const rolesArray = Array.isArray(roles) ? roles : [];
        const role = rolesArray[0]?.role ?? '';
        const scope = rolesArray[0]?.scope ?? '';
        return [actor, action, user, environment, role, scope].some((field) =>
          field.toLowerCase().includes(lowerSearchTerm),
        );
      });
    }, [allRows, searchTerm]);

    const sortedTableData = useMemo(
      () => sortAuditLogs(filteredTableData, sortByState),
      [filteredTableData, sortByState],
    );

    const shouldKeepEmptyStateWhileLoading =
      searchTerm && !filteredTableData.length && hasMoreData && isLoading;
    const showTableSkeleton =
      ((isLoading && !tableData.length) || reloadLoading) &&
      !shouldKeepEmptyStateWhileLoading;
    const showPaginationLoading = isLoading && !!tableData.length;

    if (isError) {
      return (
        <Flex flex={1} justifyContent='center' alignItems='center'>
          <Stack
            align='center'
            textAlign='center'
            spacing='6'
            maxW='sm'
            data-testid='audit-logs-table-error-state'
          >
            <Image
              boxSize='32'
              src='https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-alert-circle'
              alt='error'
            />
            <Stack spacing='2'>
              <Heading as='h2' size='lg'>
                {hasRetried
                  ? `Retry failed with error code ${
                      getErrorDetails()?.status || 'Unknown'
                    }`
                  : 'Failed to load audit logs'}
              </Heading>
              <Text variant='small'>
                {hasRetried
                  ? getErrorDetails()?.detail
                    ? `${getErrorDetails()?.detail}`
                    : 'Logs unavailable'
                  : 'Logs unavailable'}
              </Text>
            </Stack>
            <Text maxWidth='sm'>
              {hasRetried ? (
                <>
                  If this error persists, try visiting the{' '}
                  <Link
                    mb={4}
                    href='https://support.sitecore.com/kb'
                    isExternal
                  >
                    Knowledge Base
                  </Link>{' '}
                  for assistance or reach out to Sitecore Support team.
                </>
              ) : tenantId ? (
                'Unable to load audit logs for the selected product and tenant. Please try again.'
              ) : (
                'Unable to load audit logs for the selected product. Please try again.'
              )}
            </Text>
            <Button
              variant='link'
              onClick={() => {
                setHasRetried(true);
                refetch();
              }}
            >
              Retry
            </Button>
          </Stack>
        </Flex>
      );
    }

    const isSearchingWithNoResults = !filteredTableData.length && searchTerm;

    const shouldShowEmptyState =
      !filteredTableData.length &&
      !reloadLoading &&
      (!isLoading || isSearchingWithNoResults);

    if (shouldShowEmptyState) {
      // Check if we have original data but filtered results are empty (search with no matches)
      const hasOriginalData = allRows.length > 0;
      const hasMoreDataAndSearching = searchTerm && hasMoreData;

      let emptyStateText: string;
      let emptyStateDescription: string | undefined;

      if (hasOriginalData && searchTerm && !hasMoreDataAndSearching) {
        emptyStateText = 'No results found for your search';
        emptyStateDescription =
          'Try adjusting your search term or clear the search to see all results';
      } else {
        emptyStateText = tenantId
          ? 'No audit logs for the selected product and tenant'
          : 'No audit logs for the selected product';
      }

      // Empty state for search with no results and load more button
      if (hasMoreDataAndSearching) {
        return (
          <Flex
            flexDir='column'
            justifyContent='center'
            alignItems='center'
            m='auto'
            data-testid='no-audit-logs-container'
            gap={4}
          >
            <Flex alignItems='center' direction='column' gap={3}>
              <Heading size='md' textAlign='center'>
                No results found for your search
              </Heading>
              <Text variant='subtle' textAlign='center' fontSize='sm'>
                Load more results to expand your search scope or adjust your
                search term
              </Text>
              <Button
                variant='outline'
                size='md'
                onClick={() =>
                  setContinuationToken(responseData?.continuationToken ?? '')
                }
                isLoading={isLoading}
                data-testid='empty-state-load-more-button'
              >
                Load More
              </Button>
            </Flex>
          </Flex>
        );
      }

      return (
        <EmptyState
          text={emptyStateText}
          description={emptyStateDescription}
          dataTestId='no-audit-logs-container'
        />
      );
    }

    return (
      <>
        <Box overflowX='auto'>
          <DataTable
            columns={tableOptions.columns}
            data={sortedTableData}
            isLoading={showTableSkeleton}
            autoSort={false}
            sortBy={sortByState}
            onChange={handleTableChange}
            page={1}
            pagination='hidden'
            data-testid='audit-logs-table'
            onClickRow={(row: AuditLog) => setSelectedLog(row)}
            selectedRow={selectedLog}
            paginationProps={{
              'data-behavior-analytics-feature': 'audit logs pagination',
            }}
            data-behavior-analytics-feature='audit logs table'
          />
        </Box>
        {((hasMoreData && !showTableSkeleton) || showPaginationLoading) && (
          <LoadMoreButton
            onClick={() =>
              setContinuationToken(responseData?.continuationToken ?? '')
            }
            isLoading={showPaginationLoading}
          />
        )}
        <AuditLogDetailsDrawer
          data={selectedLog}
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      </>
    );
  },
);
